import type { Theme } from "@mui/material/styles/createTheme";
import {
  createTheme as createMuiTheme,
  responsiveFontSizes,
} from "@mui/material/styles";

import { createOptions as createBaseOptions } from "./base/create-options";
import { createOptions as createDarkOptions } from "./dark/create-options";
import { createOptions as createLightOptions } from "./light/create-options";

declare module "@mui/material/styles/createPalette" {
  interface ColorRange {
    50: string;
    100: string;
    200: string;
    300: string;
    400: string;
    500: string;
    600: string;
    700: string;
    800: string;
    900: string;
  }

  interface MobilePalette {
    ePurple: { 50: string; 500: string; 700: string };
    peach: { 100: string; 200: string };
    orange: { 500: string };
    coral: { 500: string };
    base: {
      50: string;
      100: string;
      200: string;
      300: string;
      500: string;
      900: string;
    };
    grey: {
      50: string;
      100: string;
      200: string;
      400: string;
      700: string;
      900: string;
    };
    surface: { white: string; 100: string };
    red: { 50: string; 400: string; 500: string; 700: string };
    blue: { 50: string; 500: string };
    docCard: { lavender: string; sky: string; peach: string };
    green: { 500: string };
    yellow: { 100: string; 800: string };
  }

  interface SignerHighlight {
    main: string;
    chipBg: string;
    chipText: string;
    check: string;
  }

  interface SignerPalette {
    ink: string;
    signatureInk: string;
    docTitle: string;
    docText: string;
    orange: {
      main: string;
      dark: string;
      darker: string;
      muted: string;
      border: string;
      tint: string;
      faint: string;
      faintest: string;
      hover: string;
      progressTrack: string;
    };
    purple: {
      main: string;
      dark: string;
      light: string;
      border: string;
      borderStrong: string;
      tint: string;
      faint: string;
    };
    success: { main: string; border: string; tint: string; faint: string };
    danger: {
      main: string;
      dark: string;
      border: string;
      borderStrong: string;
      tint: string;
    };
    warn: { main: string; text: string; border: string; tint: string };
    text: {
      secondary: string;
      meta: string;
      muted: string;
      placeholder: string;
    };
    surface: { canvas: string; subtle: string; muted: string };
    border: { light: string; main: string; strong: string };
    disabled: string;
    gradient: { coral: string; amber: string };
    highlight: {
      yellow: SignerHighlight;
      orange: SignerHighlight;
      red: SignerHighlight;
      blue: SignerHighlight;
    };
  }

  interface ChartPalette {
    series: {
      requested: string;
      sent: string;
      opened: string;
      completed: string;
    };
    status: {
      pending: string;
      signed: string;
      scheduled: string;
      draft: string;
      declined: string;
      rejected: string;
      inReview: string;
      voided: string;
      expired: string;
      fallback: string;
    };
    grid: string;
    axis: string;
    cursor: string;
    track: string;
    canvas: string;
    panelBorder: string;
    cardBorder: string;
    label: string;
    cardShadow: string;
    tooltipShadow: string;
  }

  interface FormFieldPalette {
    label: string;
    labelMuted: string;
    required: string;
    bg: string;
    border: string;
    focusBorder: string;
    text: string;
    placeholder: string;
    adornment: string;
    disabledBg: string;
    disabledBorder: string;
    disabledText: string;
  }

  interface NotificationVariant {
    bg: string;
    border: string;
    icon: string;
    title: string;
    text: string;
  }

  interface NotificationPalette {
    success: NotificationVariant;
    info: NotificationVariant;
    warning: NotificationVariant;
    error: NotificationVariant;
    shadow: string;
  }

  interface SplitButtonPalette {
    bg: string;
    hoverBg: string;
    color: string;
    radius: string;
    padding: string;
    fontSize: string;
    fontWeight: number;
    gap: string;
    iconSize: number;
    caretWidth: number;
    caretBg: string;
    caretHoverBg: string;
    caretDivider: string;
    caretIconSize: number;
  }

  interface SwitchControlPalette {
    trackOff: string;
    trackOn: string;
    knob: string;
    knobShadow: string;
    trackOffDisabled: string;
    trackOnDisabled: string;
    focusRing: string;
    focusRingWidth: string;
    width: number;
    height: number;
    knobSize: number;
    knobInset: number;
    knobTravel: number;
    trackRadius: string;
    small: {
      width: number;
      height: number;
      knobSize: number;
      knobInset: number;
      knobTravel: number;
    };
    trackTransition: string;
    knobTransition: string;
  }

  interface TablePalette {
    surface: string;
    border: string;
    divider: string;
    shadow: string;
    header: {
      bg: string;
      border: string;
      text: string;
      activeText: string;
    };
    row: {
      text: string;
      secondaryText: string;
      hover: string;
      selected: string;
    };
    caption: {
      title: string;
      description: string;
    };
    icon: {
      rest: string;
      hover: string;
    };
    selection: {
      rest: string;
      active: string;
      countText: string;
    };
    empty: {
      text: string;
    };
    pagination: {
      border: string;
      text: string;
      hover: string;
      selectedBg: string;
      selectedText: string;
      ellipsis: string;
      disabled: string;
    };
  }

  interface Palette {
    neutral: ColorRange;
    mobile: MobilePalette;
    signer: SignerPalette;
    notification: NotificationPalette;
    chart: ChartPalette;
    formField: FormFieldPalette;
    splitButton: SplitButtonPalette;
    switchControl: SwitchControlPalette;
    table: TablePalette;
    premium: string;
    brandGradient: string;
  }

  interface PaletteColor {
    lightest?: string;
    darkest?: string;
    alpha4?: string;
    alpha8?: string;
    alpha12?: string;
    alpha30?: string;
    alpha50?: string;
  }

  interface PaletteOptions {
    neutral?: ColorRange;
    mobile?: MobilePalette;
    signer?: SignerPalette;
    notification?: NotificationPalette;
    chart?: ChartPalette;
    formField?: FormFieldPalette;
    splitButton?: SplitButtonPalette;
    switchControl?: SwitchControlPalette;
    table?: TablePalette;
    premium?: string;
    brandGradient?: string;
  }

  interface TypeBackground {
    paper: string;
    default: string;
  }

  interface TypeText {
    placeholder: string;
  }
}

export type ColorPreset = "inkfree";

export type Contrast = "normal" | "high";

export type Direction = "ltr" | "rtl";

export type PaletteMode = "dark" | "light";

interface ThemeConfig {
  colorPreset?: ColorPreset;
  contrast?: Contrast;
  direction?: Direction;
  paletteMode?: PaletteMode;
  responsiveFontSizes?: boolean;
}

export const createTheme = (config: ThemeConfig): Theme => {
  let theme = createMuiTheme(
    // Base options available for both dark and light palette modes
    createBaseOptions({
      direction: config.direction,
    }),
    // Options based on selected palette mode, color preset and contrast
    createLightOptions({
      colorPreset: config.colorPreset,
      contrast: config.contrast,
    }),
    // config.paletteMode === "dark"
    //   ? createDarkOptions({
    //       colorPreset: config.colorPreset,
    //       contrast: config.contrast,
    //     })
    //   : createLightOptions({
    //       colorPreset: config.colorPreset,
    //       contrast: config.contrast,
    //     })
  );

  if (config.responsiveFontSizes) {
    theme = responsiveFontSizes(theme);
  }

  return theme;
};
